import { useQuery } from '@tanstack/react-query'

export interface IndustryReferenceItem {
  ID: number
  'Reference/Framework': string
  referenceLink: string
  Industry: string
  Description: string
  "Where it's used": string
}

export interface IndustryReferenceResponse {
  data: IndustryReferenceItem[]
}

export const useIndustryReferenceQuery = () => {
  return useQuery<IndustryReferenceResponse>({
    queryKey: ['industry-reference'],
    queryFn: async () => {
      const mockData: IndustryReferenceItem[] = [
        {
          ID: 1,
          'Reference/Framework': 'GDPR',
          referenceLink: 'https://gdpr-info.eu/',
          Industry: 'Cross-Industry',
          Description: 'A comprehensive data privacy law passed by the European Union (EU).',
          "Where it's used": 'Define PII Tags across schemas, tables and columns.',
        },
        {
          ID: 2,
          'Reference/Framework': 'DAMA-DMBOK',
          referenceLink: 'https://www.dama.org/cpages/body-of-knowledge',
          Industry: 'Cross-Industry',
          Description: 'Guide to data governance and metadata management.',
          "Where it's used": 'Define metadata types, glossary, and rules for any industry.',
        },
        {
          ID: 3,
          'Reference/Framework': 'HL7/FHIR',
          referenceLink: 'https://www.hl7.org/fhir/',
          Industry: 'Healthcare & Life Sciences',
          Description: 'Interoperability standard for healthcare data.',
          "Where it's used": 'Define and link healthcare resource metadata.',
        },
        {
          ID: 4,
          'Reference/Framework': 'BIAN',
          referenceLink: 'https://www.bian.org/',
          Industry: 'Banking & Financial Services',
          Description: 'Semantic banking services and capability framework.',
          "Where it's used": 'Standardize business capabilities and metadata in banking systems.',
        },
        {
          ID: 5,
          'Reference/Framework': 'Open Banking',
          referenceLink: 'https://www.openbanking.org.uk/',
          Industry: 'Banking & Financial Services',
          Description: 'Secure API/data exchange frameworks for banking.',
          "Where it's used":
            'Defines APIs, schemas, and business terms for account and transaction data.',
        },
        {
          ID: 6,
          'Reference/Framework': 'FIBO',
          referenceLink: 'https://spec.edmcouncil.org/fibo/',
          Industry: 'Banking & Financial Services',
          Description: 'Ontology for financial services data.',
          "Where it's used": 'Enrich metadata with standard financial business concepts.',
        },
        {
          ID: 7,
          'Reference/Framework': 'ISO 20022',
          referenceLink: 'https://www.iso20022.org/',
          Industry: 'Banking & Financial Services',
          Description: 'Standard for electronic messaging in payments.',
          "Where it's used": 'Messaging structure, metadata, and rules for financial transactions.',
        },
        {
          ID: 8,
          'Reference/Framework': 'LOINC',
          referenceLink: 'https://loinc.org/',
          Industry: 'Healthcare & Life Sciences',
          Description: 'Standard codes for lab results and measurements.',
          "Where it's used": 'Standardize lab and clinical observation datasets.',
        },
        {
          ID: 9,
          'Reference/Framework': 'SNOMED CT',
          referenceLink: 'https://www.snomed.org/',
          Industry: 'Healthcare & Life Sciences',
          Description: 'Clinical terminology system.',
          "Where it's used": 'Use for diagnosis, procedures, and metadata tagging.',
        },
        {
          ID: 10,
          'Reference/Framework': 'CDISC',
          referenceLink: 'https://www.cdisc.org/standards',
          Industry: 'Healthcare & Life Sciences',
          Description: 'Standards for clinical research.',
          "Where it's used": 'Clinical trial data structuring and regulatory submission.',
        },
        {
          ID: 11,
          'Reference/Framework': 'GS1 Standards',
          referenceLink: 'https://www.gs1.org/standards',
          Industry: 'Retail & Consumer Goods',
          Description: 'Product, party, and location identification.',
          "Where it's used": 'Enrich product master data and supply chain metadata.',
        },
        {
          ID: 12,
          'Reference/Framework': 'OAGIS',
          referenceLink: 'https://www.oagi.org/oagis',
          Industry: 'Retail & Consumer Goods',
          Description: 'Business process and message standards.',
          "Where it's used": 'Define data and process semantics across ERP/supply chain systems.',
        },
        {
          ID: 13,
          'Reference/Framework': 'TM Forum SID',
          referenceLink: 'https://www.tmforum.org/sid/',
          Industry: 'Telecommunications',
          Description: 'Shared Information/Data Model for telecom.',
          "Where it's used": 'Service order, customer metadata, and operational rules.',
        },
        {
          ID: 14,
          'Reference/Framework': '3GPP',
          referenceLink: 'https://www.3gpp.org/',
          Industry: 'Telecommunications',
          Description: 'Standards for mobile telecom systems.',
          "Where it's used": 'Metadata enrichment for network/service-level telecom data.',
        },
        {
          ID: 15,
          'Reference/Framework': 'CIM (IEC)',
          referenceLink: 'https://www.iec.ch/standards/61970',
          Industry: 'Energy & Utilities',
          Description: 'Common Information Model for utilities.',
          "Where it's used": 'Power system data modeling, operational metadata.',
        },
        {
          ID: 16,
          'Reference/Framework': 'NAESB',
          referenceLink: 'https://www.naesb.org/',
          Industry: 'Energy & Utilities',
          Description: 'Business practice standards for energy markets.',
          "Where it's used": 'Market and transaction metadata rules.',
        },
        {
          ID: 17,
          'Reference/Framework': 'OpenADR',
          referenceLink: 'https://www.openadr.org/',
          Industry: 'Energy & Utilities',
          Description: 'Demand response communication standard.',
          "Where it's used": 'Metadata for DR signals and load events.',
        },
        {
          ID: 18,
          'Reference/Framework': 'NIEM',
          referenceLink: 'https://www.niem.gov/',
          Industry: 'Public Sector / Government',
          Description: 'Standardized data exchange across agencies.',
          "Where it's used": 'Public sector metadata enrichment and harmonization.',
        },
        {
          ID: 19,
          'Reference/Framework': 'DAMA-DMBOK',
          referenceLink: 'https://www.dama.org/cpages/body-of-knowledge',
          Industry: 'Cross-Industry',
          Description: 'Guide to data governance and metadata management.',
          "Where it's used": 'Define metadata types, glossary, and rules for any industry.',
        },
        {
          ID: 20,
          'Reference/Framework': 'EDM Council DCAM',
          referenceLink: 'https://edmcouncil.org/page/DCAM',
          Industry: 'Cross-Industry',
          Description: 'Capability model for data governance and metadata.',
          "Where it's used": 'Enrich enterprise metadata architecture and policies.',
        },
        {
          ID: 21,
          'Reference/Framework': 'W3C RDF/OWL/SHACL',
          referenceLink: 'https://www.w3.org/standards/semanticweb/',
          Industry: 'Cross-Industry',
          Description: 'Semantic web data modeling languages.',
          "Where it's used": 'Ontology-based metadata modeling, validation, and rules.',
        },
      ]

      return { data: mockData }
    },
  })
}
